(function () {
  'use strict';

  const FEE_VARIANT_ID = '55420858270081';
  const FEE_ROW_SELECTOR = `.cart-item[data-variant-id="${FEE_VARIANT_ID}"]`;
  const PANEL_ATTR = 'data-digitisation-fee-inline';
  const STYLE_ID = 'new-logo-digitisation-inline-style';
  const DIGITISATION_INLINE_SYNCED_EVENT = 'new-logo:digitisation-inline-synced';

  let refreshTimer = null;
  let drawerObserver = null;
  let cartItemsObserver = null;
  let observedCartItemsElement = null;
  let latestCartPayload = null;

  function emitDigitisationInlineSynced(reason) {
    document.dispatchEvent(
      new CustomEvent(DIGITISATION_INLINE_SYNCED_EVENT, {
        detail: {
          reason: String(reason || ''),
          at: Date.now(),
        },
      })
    );
  }

  function toWholeNumber(value) {
    const parsed = Number.parseInt(String(value ?? '').trim(), 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function toCents(value) {
    const raw = String(value ?? '').trim();
    if (!raw) return 0;
    if (/^-?\d+$/.test(raw)) {
      const parsed = Number.parseInt(raw, 10);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    const normalized = raw.replace(/[^0-9,.-]/g, '').replace(/,/g, '');
    const parsed = Number.parseFloat(normalized);
    if (!Number.isFinite(parsed)) return 0;
    return Math.round(parsed * 100);
  }

  function formatMoney(cents) {
    const safeCents = Math.round(Number(cents) || 0);
    const themeFormat = window.FoxTheme?.settings?.moneyFormat;
    const formatter = window.FoxTheme?.utils?.formatMoney;

    if (typeof formatter === 'function' && themeFormat) {
      try {
        const formatted = String(formatter(safeCents, themeFormat) || '').trim();
        if (formatted) return formatted;
      } catch (error) {
        // no-op
      }
    }

    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(safeCents / 100);
  }

  function ensureStyles() {
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .new-logo-digitisation-inline {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        padding: 1rem 1.1rem;
        min-height: 4.4rem;
        margin-bottom: 0.75rem;
        border: 1px solid rgba(29, 52, 154, 0.18);
        border-radius: 0.5rem;
        background: #fff;
      }

      .new-logo-digitisation-inline__title {
        margin: 0;
        font-size: 1.22rem;
        line-height: 1.15;
        font-weight: 600;
        color: #1d349a;
      }

      .new-logo-digitisation-inline__meta {
        margin: 0.08rem 0 0;
        font-size: 1.08rem;
        line-height: 1.1;
        color: #6b7280;
      }

      .new-logo-digitisation-inline__price {
        font-size: 1.38rem;
        line-height: 1;
        font-weight: 700;
        color: #111827;
        white-space: nowrap;
      }
    `;

    document.head.appendChild(style);
  }

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function getFeeRows(drawer) {
    if (!drawer) return [];
    return Array.from(drawer.querySelectorAll(FEE_ROW_SELECTOR));
  }

  function getQtyFromRow(row) {
    if (!row) return 0;
    const inputQty = toWholeNumber(row.querySelector('.quantity__input')?.value);
    if (inputQty > 0) return inputQty;

    const dataQty = toWholeNumber(row.getAttribute('data-quantity'));
    if (dataQty > 0) return dataQty;

    return 0;
  }

  function getUnitPriceFromRow(row) {
    if (!row) return 0;
    const attrPrice = toCents(row.getAttribute('data-price'));
    if (attrPrice > 0) return attrPrice;

    const moneyText = row.querySelector('.cart-item__prices .price, .cart-item__total .font-body-bolder')?.textContent || '';
    return toCents(moneyText);
  }

  function getCartPayload(candidate) {
    if (!candidate || typeof candidate !== 'object') return null;
    if (!Array.isArray(candidate.items)) return null;
    return candidate;
  }

  function isTruthySetupRequired(value) {
    const normalized = String(value ?? '').trim().toLowerCase();
    return normalized === 'yes' || normalized === 'true' || normalized === '1';
  }

  function getSetupFeeUnitFallbackFromConfig() {
    const raw = window.NewLogoConfig?.prices?.digitalisationCents;
    const parsed = toCents(raw);
    return parsed > 0 ? parsed : 0;
  }

  function resolveLegacyLogoIdentity(properties) {
    const source = properties || {};
    const method = String(source._logo_method || source['Logo Method'] || '').trim().toLowerCase();
    const sizeTier = String(source._logo_size_tier || source['Logo Size'] || '').trim().toLowerCase();
    if (!method) return '';

    const artworkUrls = String(source._logo_artwork_urls || '')
      .split('|')
      .map((value) => String(value || '').trim().toLowerCase())
      .filter(Boolean);
    if (!artworkUrls.length) {
      [source._logo_artwork_url, source._logo_artwork, source._logo_artwork_2, source._logo_artwork_3]
        .map((value) => String(value || '').trim().toLowerCase())
        .filter(Boolean)
        .forEach((value) => artworkUrls.push(value));
    }
    const artworkNames = String(source._logo_artwork_filenames || source._logo_artwork_filename || '')
      .split('|')
      .map((value) => String(value || '').trim().toLowerCase())
      .filter(Boolean);
    const artworkSignature = artworkUrls.join('|') || artworkNames.join('|');
    const signature = `${artworkSignature}|${method}|${sizeTier}`.trim();
    return signature ? `legacy-logo:${signature}` : '';
  }

  function getRequiredSetupFeeQtyFromCart(cart) {
    const safeCart = getCartPayload(cart);
    if (!safeCart) return null;

    const setupGroups = new Map();
    (safeCart.items || []).forEach((item, index) => {
      const variantId = String(item?.variant_id ?? item?.id ?? '').trim();
      if (variantId === FEE_VARIANT_ID) return;

      const properties = item?.properties || {};
      if (properties._logo_charge_type != null || properties._logo_parent_key != null) return;

      const logoType = String(properties._logo_type || properties['Logo Type'] || '').trim().toLowerCase();
      if (logoType && logoType !== 'logo') return;
      const setupRequired = isTruthySetupRequired(properties._logo_setup_required || properties['Logo Setup Required']);
      const explicitGroupSetupFee = toCents(properties._logo_setup_group_fee_cents);
      const hasSetupSignal = setupRequired || explicitGroupSetupFee > 0;
      if (!hasSetupSignal) return;

      const explicitSetupFee = toCents(properties._logo_setup_fee_cents);
      const method = String(properties._logo_method || properties['Logo Method'] || '').trim().toLowerCase();
      const fallbackSetupFee = setupRequired && method === 'embroidery' ? getSetupFeeUnitFallbackFromConfig() : 0;
      const setupFeeCents =
        explicitGroupSetupFee > 0 ? explicitGroupSetupFee : explicitSetupFee > 0 ? explicitSetupFee : fallbackSetupFee;
      if (setupFeeCents <= 0) return;
      const explicitSetupUnits = toWholeNumber(properties._logo_setup_units);
      const setupUnits = explicitSetupUnits > 0 ? explicitSetupUnits : 1;

      const explicitToken = String(properties._logo_customiser_token || '').trim();
      if (explicitToken) {
        const previous = setupGroups.get(explicitToken) || 0;
        setupGroups.set(explicitToken, Math.max(previous, setupUnits));
        return;
      }

      const legacyIdentity = resolveLegacyLogoIdentity(properties);
      if (legacyIdentity) {
        const previous = setupGroups.get(legacyIdentity) || 0;
        setupGroups.set(legacyIdentity, Math.max(previous, setupUnits));
        return;
      }

      const lineKey = String(item?.key || '').trim();
      if (lineKey) {
        const previous = setupGroups.get(lineKey) || 0;
        setupGroups.set(lineKey, Math.max(previous, setupUnits));
        return;
      }

      const groupToken = String(properties._logo_customisation_group || '').trim();
      const fallbackToken = groupToken || `line-${index}`;
      const previous = setupGroups.get(fallbackToken) || 0;
      setupGroups.set(fallbackToken, Math.max(previous, setupUnits));
    });

    return Array.from(setupGroups.values()).reduce((sum, units) => sum + units, 0);
  }

  function getFeeInfoFromCart(cart) {
    const safeCart = getCartPayload(cart);
    if (!safeCart) return { qty: 0, unitPriceCents: 0 };

    const feeItems = (safeCart.items || []).filter((item) => String(item?.variant_id ?? item?.id ?? '') === FEE_VARIANT_ID);
    if (!feeItems.length) return { qty: 0, unitPriceCents: 0 };

    const qty = feeItems.reduce((sum, item) => sum + toWholeNumber(item?.quantity), 0);
    const firstItem = feeItems[0] || {};
    const unitPriceCents =
      toCents(firstItem.final_price) ||
      toCents(firstItem.price) ||
      (qty > 0 ? Math.round(toCents(firstItem.final_line_price) / qty) : 0);

    return {
      qty,
      unitPriceCents,
    };
  }

  function hideFeeRows(feeRows) {
    feeRows.forEach((row) => {
      row.setAttribute('aria-hidden', 'true');
      row.style.display = 'none';
      row.dataset.digitisationFeeHidden = 'true';
    });
  }

  function hideFeeRowsNow(scope) {
    const root = scope && scope.querySelectorAll ? scope : document;
    hideFeeRows(Array.from(root.querySelectorAll(FEE_ROW_SELECTOR)));
  }

  function removeInlinePanel(drawer) {
    if (!drawer) return;
    drawer.querySelectorAll(`[${PANEL_ATTR}]`).forEach((panel) => panel.remove());
  }

  function resolveInsertionAnchor(drawer) {
    const cartDrawerBody = drawer.querySelector('.drawer__body');
    const bodyContent = cartDrawerBody?.querySelector('.drawer__body-inner');
    const cartItems = drawer.querySelector('cart-items');
    if (bodyContent && cartItems && cartItems.parentElement === bodyContent) {
      return { parent: bodyContent, before: cartItems };
    }

    if (cartDrawerBody && cartItems && cartItems.parentElement === cartDrawerBody) {
      return { parent: cartDrawerBody, before: cartItems };
    }

    const parent = cartItems?.parentElement;
    if (cartItems && parent) {
      return { parent, before: cartItems };
    }

    return null;
  }

  function renderInlinePanel(drawer, qty, unitPriceCents) {
    const target = resolveInsertionAnchor(drawer);
    if (!target || !target.parent) return;

    let panel = target.parent.querySelector(`[${PANEL_ATTR}]`);
    if (!panel) {
      panel = document.createElement('div');
      panel.setAttribute(PANEL_ATTR, 'true');
      panel.className = 'new-logo-digitisation-inline';
    }

    const totalCents = Math.max(0, qty) * Math.max(0, unitPriceCents);
    const meta = qty > 1 ? `Qty ${qty} · ${formatMoney(unitPriceCents)} each` : 'Qty 1';

    panel.innerHTML = `
      <div>
        <p class="new-logo-digitisation-inline__title">Digitalisation fee</p>
        <p class="new-logo-digitisation-inline__meta">${meta}</p>
      </div>
      <div class="new-logo-digitisation-inline__price">${formatMoney(totalCents || unitPriceCents)}</div>
    `;

    if (target.before) {
      target.parent.insertBefore(panel, target.before);
    } else {
      target.parent.appendChild(panel);
    }
  }

  function syncDigitisationFeeInline(cartHint) {
    const drawer = getCartDrawer();
    if (!drawer) return;

    ensureStyles();

    const safeCart = getCartPayload(cartHint || latestCartPayload);
    const requiredQty = getRequiredSetupFeeQtyFromCart(safeCart);
    const feeRows = getFeeRows(drawer);
    let qty = 0;
    let unitPriceCents = 0;

    if (feeRows.length) {
      hideFeeRows(feeRows);
      qty = feeRows.reduce((sum, row) => sum + getQtyFromRow(row), 0);
      unitPriceCents = getUnitPriceFromRow(feeRows[0]);
    }

    if (typeof requiredQty === 'number') {
      qty = Math.max(0, requiredQty);
    } else if (qty <= 0) {
      const fromCart = getFeeInfoFromCart(safeCart);
      qty = fromCart.qty;
      unitPriceCents = unitPriceCents > 0 ? unitPriceCents : fromCart.unitPriceCents;
    }

    if (unitPriceCents <= 0) {
      const fromCart = getFeeInfoFromCart(safeCart);
      unitPriceCents = fromCart.unitPriceCents;
    }

    if (unitPriceCents <= 0) {
      unitPriceCents = getSetupFeeUnitFallbackFromConfig();
    }

    if (qty <= 0 || unitPriceCents <= 0) {
      removeInlinePanel(drawer);
      return;
    }

    renderInlinePanel(drawer, qty, unitPriceCents);
  }

  function scheduleSync(delay, cartHint) {
    const payload = getCartPayload(cartHint);
    if (payload) {
      latestCartPayload = payload;
    }

    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      hideFeeRowsNow(document);
      setupDrawerObserver();
      setupCartItemsObserver();
      try {
        syncDigitisationFeeInline(payload || latestCartPayload);
        emitDigitisationInlineSynced('completed');
      } catch (error) {
        console.error(error);
        emitDigitisationInlineSynced('error');
      }
    }, delay);
  }

  function syncNow(reason, cartHint) {
    const payload = getCartPayload(cartHint);
    if (payload) {
      latestCartPayload = payload;
    }

    hideFeeRowsNow(document);
    setupDrawerObserver();
    setupCartItemsObserver();
    try {
      syncDigitisationFeeInline(payload || latestCartPayload);
      emitDigitisationInlineSynced(reason || 'completed');
    } catch (error) {
      console.error(error);
      emitDigitisationInlineSynced('error');
    }
  }

  function setupDrawerObserver() {
    const drawer = getCartDrawer();
    if (!drawer) return;
    if (drawerObserver) return;

    drawerObserver = new MutationObserver((mutations) => {
      if (!mutations.some((mutation) => mutation.attributeName === 'open')) return;
      scheduleSync(30);
    });
    drawerObserver.observe(drawer, {
      attributes: true,
      attributeFilter: ['open'],
    });
  }

  function setupCartItemsObserver() {
    const drawer = getCartDrawer();
    const cartItemsElement = drawer?.querySelector('cart-items');

    if (!drawer || !cartItemsElement) {
      if (cartItemsObserver) {
        cartItemsObserver.disconnect();
        cartItemsObserver = null;
      }
      observedCartItemsElement = null;
      return;
    }

    if (observedCartItemsElement === cartItemsElement && cartItemsObserver) return;

    if (cartItemsObserver) {
      cartItemsObserver.disconnect();
    }

    observedCartItemsElement = cartItemsElement;
    cartItemsObserver = new MutationObserver((mutations) => {
      if (!mutations.some((mutation) => mutation.type === 'childList')) return;
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!node || node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.matches?.(FEE_ROW_SELECTOR)) {
            hideFeeRows([node]);
            return;
          }
          hideFeeRowsNow(node);
        });
      });
      scheduleSync(0, latestCartPayload);
    });

    cartItemsObserver.observe(cartItemsElement, {
      childList: true,
      subtree: true,
    });
  }

  function init() {
    hideFeeRowsNow(document);
    setupDrawerObserver();
    setupCartItemsObserver();
    scheduleSync(0);

    document.addEventListener('cart:updated', (event) => {
      syncNow('completed', event?.detail?.cart || null);
    });
    document.addEventListener('cart:refresh', () => {
      hideFeeRowsNow(document);
      scheduleSync(10, latestCartPayload);
    });
    document.addEventListener('logo-customiser:saved', () => {
      hideFeeRowsNow(document);
      scheduleSync(30, latestCartPayload);
    });
    document.addEventListener('new-logo:digitisation-fee-reconciled', (event) => {
      hideFeeRowsNow(document);
      scheduleSync(0, event?.detail?.cart || latestCartPayload);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
